import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { API_BASE } from '../lib/config';
const API = `${API_BASE}`;

// デバイス検出ユーティリティ
const detectDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
  const isAndroid = /android/i.test(userAgent);
  
  return { isIOS, isAndroid };
};

// カスタムマーカーアイコン
const createCustomIcon = (isCurrentEmployer = false) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background-color: ${isCurrentEmployer ? '#ef4444' : '#3b82f6'};
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        color: white;
        font-weight: bold;
      ">
        ${isCurrentEmployer ? '' : ''}
      </div>
    `,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12]
  });
};

// iOS用Apple Maps開くリンク
const openAppleMaps = (latitude, longitude, name) => {
  const url = `maps://maps.apple.com/?q=${encodeURIComponent(name)}&ll=${latitude},${longitude}&z=15`;
  console.log('Apple Maps URL:', url);
  window.open(url, '_blank');
};

// Android/PC用Google Maps開くリンク
const openGoogleMaps = (latitude, longitude, name) => {
  const url = `https://maps.google.com/?q=${encodeURIComponent(name)}&ll=${latitude},${longitude}&z=15`;
  console.log('Google Maps URL:', url);
  window.open(url, '_blank');
};

// 住所をクリップボードにコピー
const copyAddressToClipboard = (address) => {
  console.log('住所をコピー開始:', address);
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(address).then(() => {
      console.log('住所のコピーが成功しました (Clipboard API)');
      alert('住所をコピーしました');
    }).catch(() => {
      console.log('Clipboard API失敗、フォールバックを使用');
      // フォールバック
      fallbackCopyTextToClipboard(address);
    });
  } else {
    console.log('Clipboard API非対応、フォールバックを使用');
    // フォールバック
    fallbackCopyTextToClipboard(address);
  }
};

const fallbackCopyTextToClipboard = (text) => {
  console.log('フォールバックコピー開始:', text);
  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.style.top = "0";
  textArea.style.left = "0";
  textArea.style.position = "fixed";
  textArea.style.opacity = "0";
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  
  try {
    document.execCommand('copy');
    console.log('フォールバックコピーが成功しました');
    alert('住所をコピーしました');
  } catch (err) {
    console.log('フォールバックコピーが失敗しました:', err);
    alert('コピーに失敗しました');
  }
  
  document.body.removeChild(textArea);
};

// マップオプションモーダル
const MapOptionsModal = ({ isOpen, onClose, employer }) => {
  const { isIOS, isAndroid } = detectDevice();
  
  if (!isOpen) return null;

  const handleDeviceMapOpen = () => {
    console.log('マップで開く ボタンがクリックされました');
    if (isIOS) {
      console.log('iOS検出 - Apple Mapsで開きます');
      openAppleMaps(employer.latitude, employer.longitude, employer.business_name);
    } else {
      console.log('Android/PC検出 - Google Mapsで開きます');
      openGoogleMaps(employer.latitude, employer.longitude, employer.business_name);
    }
    onClose();
  };

  const handleGoogleMapOpen = () => {
    console.log('Google マップで開く ボタンがクリックされました');
    openGoogleMaps(employer.latitude, employer.longitude, employer.business_name);
    onClose();
  };

  const handleCopyAddress = () => {
    console.log('住所をコピーする ボタンがクリックされました');
    const address = employer.location || `${employer.prefecture || ''} ${employer.city || ''} ${employer.address || ''}`.trim();
    console.log('コピーする住所:', address);
    copyAddressToClipboard(address);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50" onClick={onClose}>
      <div 
        className="bg-white rounded-t-2xl w-full max-w-md p-4 animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col space-y-3">
          {/* ヘッダー */}
          <div className="text-center py-2">
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4"></div>
            <h3 className="text-lg font-medium text-gray-900">マップを選ぶ</h3>
          </div>
          
          {/* オプション */}
          <button
            onClick={handleDeviceMapOpen}
            className="flex items-center justify-center py-4 px-4 text-blue-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <span className="text-base font-medium">
              {isIOS ? 'マップで開く' : 'マップで開く'}
            </span>
          </button>
          
          <button
            onClick={handleGoogleMapOpen}
            className="flex items-center justify-center py-4 px-4 text-blue-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <span className="text-base font-medium">Google マップで開く</span>
          </button>
          
          <button
            onClick={handleCopyAddress}
            className="flex items-center justify-center py-4 px-4 text-blue-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <span className="text-base font-medium">住所をコピーする</span>
          </button>
          
          {/* キャンセルボタン */}
          <button
            onClick={onClose}
            className="flex items-center justify-center py-4 px-4 text-blue-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors mt-2"
          >
            <span className="text-base font-medium">キャンセル</span>
          </button>
        </div>
      </div>
      
      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

// マップコンポーネント内でマーカークリック処理
const MapClickHandler = ({ nearbyEmployers, onEmployerClick }) => {
  const map = useMap();
  
  useEffect(() => {
    map.on('popupopen', (e) => {
      const employerId = e.popup.options.employerId;
      if (employerId && nearbyEmployers) {
        const employer = nearbyEmployers.find(emp => emp.id === employerId);
        if (employer) {
          // ポップアップ内のボタンイベントをセット
          setTimeout(() => {
            const detailBtn = document.querySelector(`[data-employer-id="${employerId}"]`);
            if (detailBtn) {
              detailBtn.onclick = () => onEmployerClick(employerId);
            }
          }, 100);
        }
      }
    });
  }, [map, nearbyEmployers, onEmployerClick]);

  return null;
};

const EmployerLocationMap = ({ employer, onEmployerClick }) => {
  const [nearbyEmployers, setNearbyEmployers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchNearbyEmployers();
  }, [employer]);

  const fetchNearbyEmployers = async () => {
    if (!employer?.latitude || !employer?.longitude) {
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(
        `${API}/search/employers?latitude=${employer.latitude}&longitude=${employer.longitude}&radius_km=5`,
        { headers }
      );

      if (response.ok) {
        const employers = await response.json();
        // 現在の事業者以外で位置情報があるものをフィルタ
        const filtered = employers.filter(emp => 
          emp.id !== employer.employer_id && 
          emp.latitude && 
          emp.longitude
        );
        setNearbyEmployers(filtered);
      }
    } catch (error) {
      console.error('Error fetching nearby employers:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatWorkHours = (workHours) => {
    if (!workHours) return '時間応相談';
    return workHours.replace('-', '〜');
  };

  const calculateDailyWage = (hourlyRate, workHours) => {
    if (!hourlyRate || !workHours) return '応相談';
    
    const timeMatch = workHours.match(/(\d{1,2}):(\d{2})-(\d{1,2}):(\d{2})/);
    if (timeMatch) {
      const startHour = parseInt(timeMatch[1]);
      const startMin = parseInt(timeMatch[2]);
      const endHour = parseInt(timeMatch[3]);
      const endMin = parseInt(timeMatch[4]);
      
      let totalHours = endHour - startHour;
      let totalMins = endMin - startMin;
      
      if (totalHours < 0) totalHours += 24;
      if (totalMins < 0) {
        totalHours -= 1;
        totalMins += 60;
      }
      
      const workDuration = totalHours + (totalMins / 60);
      const dailyWage = Math.floor(hourlyRate * workDuration);
      return `${dailyWage.toLocaleString()}`;
    }
    
    return `${Math.floor(hourlyRate * 8).toLocaleString()}目安`;
  };

  if (!employer?.latitude || !employer?.longitude) {
    return (
      <div className="bg-gray-50 rounded-lg p-6 text-center">
        <p className="text-gray-500">位置情報が設定されていません</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* マップコンテナ */}
      <div className="relative bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="h-64 md:h-80">
          <MapContainer
            center={[employer.latitude, employer.longitude]}
            zoom={15}
            style={{ height: '100%', width: '100%' }}
            zoomControl={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            {/* 現在の事業者マーカー */}
            <Marker 
              position={[employer.latitude, employer.longitude]}
              icon={createCustomIcon(true)}
            >
              <Popup className="custom-popup">
                <div className="text-center p-2 min-w-[200px]">
                  <h3 className="font-bold text-sm mb-1">{employer.business_name}</h3>
                  <p className="text-xs text-gray-600 mb-1">
                    {formatWorkHours(employer.work_hours)}
                  </p>
                  <p className="text-xs text-blue-600 font-medium mb-2">
                    日給目安: {calculateDailyWage(employer.hourly_rate, employer.work_hours)}
                  </p>
                  <div className="text-xs text-gray-500">現在表示中の事業者</div>
                </div>
              </Popup>
            </Marker>

            {/* 周辺事業者マーカー */}
            {nearbyEmployers.map((nearbyEmp) => (
              <Marker 
                key={nearbyEmp.id}
                position={[nearbyEmp.latitude, nearbyEmp.longitude]}
                icon={createCustomIcon(false)}
              >
                <Popup 
                  className="custom-popup"
                  employerId={nearbyEmp.id}
                >
                  <div className="text-center p-2 min-w-[200px]">
                    <h3 className="font-bold text-sm mb-1">{nearbyEmp.name}</h3>
                    <p className="text-xs text-gray-600 mb-1">
                      {formatWorkHours(nearbyEmp.representative_job?.work_hours)}
                    </p>
                    <p className="text-xs text-blue-600 font-medium mb-2">
                      日給目安: {calculateDailyWage(
                        nearbyEmp.representative_job?.hourly_rate || nearbyEmp.avg_hourly_rate,
                        nearbyEmp.representative_job?.work_hours
                      )}
                    </p>
                    <button
                      data-employer-id={nearbyEmp.id}
                      className="bg-blue-500 text-white px-3 py-1 rounded text-xs hover:bg-blue-600 transition-colors"
                    >
                      詳細を見る
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}

            <MapClickHandler 
              nearbyEmployers={nearbyEmployers} 
              onEmployerClick={onEmployerClick}
            />
          </MapContainer>
        </div>

        {/* マップ下部の情報とボタン */}
        <div className="p-4 bg-gray-50 border-t">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-sm text-gray-600">
              <span className="inline-flex items-center mr-4">
                <span className="w-3 h-3 bg-red-500 rounded-full mr-2"></span>
                現在の事業者
              </span>
              <span className="inline-flex items-center">
                <span className="w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
                周辺の事業者 ({nearbyEmployers.length}件)
              </span>
            </div>
            
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors flex items-center justify-center"
            >
              ほかのマップで開く
            </button>
          </div>
        </div>
      </div>

      {/* 近隣事業者リスト（モバイル最適化） */}
      {nearbyEmployers.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="font-semibold text-gray-900 mb-3 text-sm">周辺の求人募集中事業者</h3>
          <div className="space-y-2">
            {nearbyEmployers.slice(0, 3).map((nearbyEmp) => (
              <div 
                key={nearbyEmp.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => onEmployerClick(nearbyEmp.id)}
              >
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-sm text-gray-900 truncate">
                    {nearbyEmp.name}
                  </h4>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatWorkHours(nearbyEmp.representative_job?.work_hours)} | 
                    日給目安: {calculateDailyWage(
                      nearbyEmp.representative_job?.hourly_rate || nearbyEmp.avg_hourly_rate,
                      nearbyEmp.representative_job?.work_hours
                    )}
                  </p>
                </div>
                <div className="ml-3 flex-shrink-0">
                  <button className="text-blue-500 text-xs font-medium hover:text-blue-600">
                    詳細
                  </button>
                </div>
              </div>
            ))}
            
            {nearbyEmployers.length > 3 && (
              <div className="text-center pt-2">
                <p className="text-xs text-gray-500">
                  他に{nearbyEmployers.length - 3}件の事業者があります
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* マップオプションモーダル */}
      <MapOptionsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        employer={employer}
      />
    </div>
  );
};

export default EmployerLocationMap;